/**
 * 학원 공개 정보 본문 — 둘러보기(AcademyDetailPage)와 멤버 홈 탭에서 공용
 * 소개 · 학원 요약 · 학원비 · 위치(지도) · 소식 · 영상 · 리뷰 · 주변 학원
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoLocationOutline, IoPlayCircle, IoHeartOutline, IoChatbubbleOutline,
  IoGlobeOutline, IoLogoInstagram, IoBookOutline,
  IoNavigateOutline, IoMapOutline, IoChevronDown,
  IoCallOutline, IoBookmark, IoBookmarkOutline, IoShareOutline,
} from 'react-icons/io5';
import academyService from '../../services/academyService';
import { parseYouTubeId, ytThumb } from '../../pages/Console/AcademyForm';

const FIELD_NAME = { study: '교과 과정', sports: '운동 / 태권도', arts: '음악 / 미술' };
const POST_LIMIT = 5;

/* 요약 정보 행 — 값이 배열이면 여러 줄로 표시 */
const SummaryRow = ({ label, value }) => {
  const lines = Array.isArray(value) ? value : [value];
  return (
    <SumRow>
      <SumRowL>{label}</SumRowL>
      <SumRowVCol>
        {lines.map((l, i) => <span key={i}>{l}</span>)}
      </SumRowVCol>
    </SumRow>
  );
};

const AcademyPublicInfo = ({ academy }) => {
  const navigate = useNavigate();
  const [nearby, setNearby] = useState([]);
  const [postsOpen, setPostsOpen] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    academyService.getApprovedAcademies().then((all) => {
      if (alive) setNearby(all.filter((x) => x.id !== academy.id).slice(0, 8));
    });
    return () => { alive = false; };
  }, [academy.id]);

  // 위치 카카오맵
  useEffect(() => {
    if (!academy?.location) return undefined;
    let cancelled = false;

    const initMap = () => {
      const kakao = window.kakao;
      if (cancelled || !kakao?.maps || !mapRef.current) return;
      kakao.maps.load(() => {
        if (cancelled || !mapRef.current) return;
        const geocoder = new kakao.maps.services.Geocoder();
        geocoder.addressSearch(academy.location, (result, status) => {
          if (cancelled || !mapRef.current) return;
          const center = (status === kakao.maps.services.Status.OK && result[0])
            ? new kakao.maps.LatLng(result[0].y, result[0].x)
            : new kakao.maps.LatLng(37.5665, 126.978);
          const map = new kakao.maps.Map(mapRef.current, { center, level: 4 });
          const marker = new kakao.maps.Marker({ position: center });
          marker.setMap(map);
          setTimeout(() => { if (!cancelled) { map.relayout(); map.setCenter(center); } }, 200);
        });
      });
    };

    if (window.kakao?.maps) {
      initMap();
      return () => { cancelled = true; };
    }
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (window.kakao?.maps) { clearInterval(timer); initMap(); }
      else if (tries > 50) clearInterval(timer);
    }, 200);
    return () => { cancelled = true; clearInterval(timer); };
  }, [academy]);

  const tuition = academy.tuition || [];
  const posts = academy.posts || [];
  const videos = academy.videos || [];
  const hashtags = academy.hashtags || [];
  const grades = academy.grades && academy.grades.length ? academy.grades : [academy.target || '-'];
  const subjects = academy.subjects && academy.subjects.length ? academy.subjects : [academy.subject];
  const hours = Array.isArray(academy.hours) ? academy.hours : [academy.hours || '-'];
  const visiblePosts = postsOpen ? posts : posts.slice(0, POST_LIMIT);
  const mapQuery = encodeURIComponent(academy.location || academy.name);

  return (
    <>
      <Tags>
        <Tag>{FIELD_NAME[academy.field] || academy.field}</Tag>
        <Tag>{academy.subject}</Tag>
        <Tag>{academy.type === 'home' ? '교습소' : academy.type === 'personal' ? '개인레슨' : '학원'}</Tag>
      </Tags>
      {hashtags.length > 0 && (
        <HashRow>{hashtags.map((h) => `#${h}`).join(' ')}</HashRow>
      )}
      <ActionRow>
        <Action
          type="button"
          onClick={() => {
            if (academy.phone) window.location.href = `tel:${academy.phone}`;
            else alert('전화번호가 준비 중이에요.');
          }}
        >
          <IoCallOutline size={22} />
          <ActionLabel>전화</ActionLabel>
        </Action>
        <Action type="button" onClick={() => alert('북마크 기능은 준비 중이에요.')}>
          <IoBookmark size={22} color="#4A7CFE" />
          <ActionLabel>{academy.likes || 0}</ActionLabel>
        </Action>
        <Action
          type="button"
          onClick={async () => {
            const url = window.location.href;
            if (navigator.share) {
              try { await navigator.share({ title: academy.name, url }); } catch {}
            } else {
              try { await navigator.clipboard.writeText(url); alert('링크가 복사되었어요.'); } catch {}
            }
          }}
        >
          <IoShareOutline size={22} />
          <ActionLabel>공유</ActionLabel>
        </Action>
      </ActionRow>

      <Section>
        <SecTitle>소개</SecTitle>
        <Intro>{academy.intro}</Intro>
      </Section>

      <Section>
        <SecTitle>학원 요약</SecTitle>
        <SumTop>
          <SumCard>
            <SumVal>{subjects.join(' · ')}</SumVal>
            <SumLabel>과목</SumLabel>
          </SumCard>
          <SumCard>
            <SumVal>{academy.type === 'home' ? '교습소' : academy.type === 'personal' ? '개인레슨' : '학원'}</SumVal>
            <SumLabel>형태</SumLabel>
          </SumCard>
        </SumTop>
        <SumList>
          <SummaryRow label="수업 대상" value={grades} />
          <SummaryRow label="영업시간" value={hours} />
          <SummaryRow label="셔틀버스" value={academy.shuttle ? '제공' : '미제공'} />
        </SumList>
      </Section>

      <Section>
        <SecTitle>학원비</SecTitle>
        {tuition.length === 0 ? <Hint>학원비는 학원에 문의해 주세요.</Hint> : (
          <Table>
            {tuition.map((t, i) => (
              <TRow key={i}>
                <TLabel>{t.label}</TLabel>
                <TPrice>{t.price}</TPrice>
              </TRow>
            ))}
          </Table>
        )}
      </Section>

      <Section>
        <SecTitle>위치</SecTitle>
        {(academy.homepage || academy.instagram || academy.blog) && (
          <LinkChips>
            {academy.homepage && (
              <LinkChip href={academy.homepage} target="_blank" rel="noreferrer">
                <IoGlobeOutline size={15} /> 홈페이지
              </LinkChip>
            )}
            {academy.instagram && (
              <LinkChip href={academy.instagram} target="_blank" rel="noreferrer">
                <IoLogoInstagram size={15} /> 인스타그램
              </LinkChip>
            )}
            {academy.blog && (
              <LinkChip href={academy.blog} target="_blank" rel="noreferrer">
                <IoBookOutline size={15} /> 블로그
              </LinkChip>
            )}
          </LinkChips>
        )}
        <InfoRow><IoLocationOutline size={17} /><span>{[academy.location, academy.addressDetail].filter(Boolean).join(' ') || '위치 정보 없음'}</span></InfoRow>
        <KakaoMap ref={mapRef} />
        <MapBtns>
          <MapBtn href={`https://map.kakao.com/?q=${mapQuery}`} target="_blank" rel="noreferrer">
            <IoNavigateOutline size={16} /> 빠른 길찾기
          </MapBtn>
          <MapBtn href={`https://map.kakao.com/?q=${mapQuery}`} target="_blank" rel="noreferrer">
            <IoMapOutline size={16} /> 지도에서 보기
          </MapBtn>
        </MapBtns>
      </Section>

      {posts.length > 0 && (
        <Section>
          <SecTitle>소식 <SecCount>{posts.length}</SecCount></SecTitle>
          {visiblePosts.map((p, i) => (
            <PostCard key={i}>
              <PostMeta>{academy.name} · {p.ago || p.date}</PostMeta>
              <PostTitle2>{p.title}</PostTitle2>
              {p.body && <PostBody>{p.body}</PostBody>}
              {(p.likes != null || p.comments != null) && (
                <PostStat>
                  <span><IoHeartOutline size={13} /> {p.likes || 0}</span>
                  <span><IoChatbubbleOutline size={13} /> {p.comments || 0}</span>
                </PostStat>
              )}
            </PostCard>
          ))}
          {posts.length > POST_LIMIT && (
            <MoreBtn type="button" onClick={() => setPostsOpen((v) => !v)}>
              {postsOpen ? '소식 접기' : `소식 더보기 ${posts.length - POST_LIMIT}개`}
              <IoChevronDown size={15} className={postsOpen ? 'up' : ''} />
            </MoreBtn>
          )}
        </Section>
      )}

      {videos.length > 0 && (
        <Section>
          <SecTitle>수업 영상</SecTitle>
          <VideoScroll>
            {videos.map((v, i) => {
              const obj = typeof v === 'string' ? { title: v } : v;
              const vid = obj.videoId || parseYouTubeId(obj.url);
              const playing = vid && playingId === vid;
              return (
                <VideoCard key={i}>
                  {playing ? (
                    <VideoFrame
                      src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&playsinline=1`}
                      title={obj.title || '수업 영상'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <VideoThumb
                      $url={vid ? ytThumb(vid) : ''}
                      $clickable={!!vid}
                      onClick={() => vid && setPlayingId(vid)}
                    >
                      <IoPlayCircle size={40} />
                    </VideoThumb>
                  )}
                  <VideoTitle>{obj.title || obj.url || ''}</VideoTitle>
                </VideoCard>
              );
            })}
          </VideoScroll>
        </Section>
      )}

      {nearby.length > 0 && (
        <Section $last>
          <SecTitle>주변 학원</SecTitle>
          <NearScroll>
            {nearby.map((n) => (
              <NearCard key={n.id} type="button" onClick={() => navigate(`/app/academy/${n.id}`, { state: { subject: n.subject } })}>
                <NearThumb $url={n.photoUrl} />
                <NearName>{n.name}</NearName>
                <NearMeta>{FIELD_NAME[n.field] || n.field} · {n.subject}</NearMeta>
              </NearCard>
            ))}
          </NearScroll>
        </Section>
      )}
    </>
  );
};

export default AcademyPublicInfo;

const Tags = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 8px;
`;

const Tag = styled.span`
  padding: 4px 9px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 600;
  color: #6B7280;
  background: #F4F5F7;
`;

const HashRow = styled.p`
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.primary};
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 10px;
  margin-top: 18px;
  padding: 6px 0 4px;
`;

const Action = styled.button`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #F2F3F7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionLabel = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Section = styled.div`
  padding: 20px 0;
  border-bottom: ${({ $last }) => ($last ? 'none' : '1px solid #F0F1F4')};
`;

const SecTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const SecCount = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Intro = styled.p`
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 8px;
  svg { color: ${({ theme }) => theme.colors.primary}; flex-shrink: 0; }
`;

const LinkChips = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const LinkChip = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 12px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  svg { color: ${({ theme }) => theme.colors.primary}; }
`;

const KakaoMap = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 12px;
  margin-top: 4px;
  background: #EDEFF3;
  overflow: hidden;
`;

const MapBtns = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const MapBtn = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 11px 0;
  border-radius: 10px;
  background: #F4F5F7;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  svg { color: ${({ theme }) => theme.colors.primary}; }
`;

const SumTop = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const SumCard = styled.div`
  flex: 1;
  min-width: 0;
  padding: 16px 10px;
  border-radius: 12px;
  background: #F4F5F7;
  text-align: center;
`;

const SumVal = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SumLabel = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
`;

const SumList = styled.div`
  border-radius: 12px;
  background: #F4F5F7;
  padding: 2px 14px;
`;

const SumRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 0;
  border-bottom: 1px solid #E7E8EC;
  &:last-child { border-bottom: none; }
`;

const SumRowL = styled.span`
  flex-shrink: 0;
  padding-top: 1px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const SumRowVCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: right;
  span {
    font-size: 13.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
`;

const TRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #F4F5F7;
  &:last-child { border-bottom: none; }
`;

const TLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TPrice = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const PostCard = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid #F0F1F4;
  &:last-of-type { border-bottom: none; }
`;

const PostMeta = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 6px;
`;

const PostTitle2 = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PostBody = styled.p`
  margin-top: 5px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostStat = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  span { display: flex; align-items: center; gap: 3px; }
`;

const MoreBtn = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 11px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 10px;
  background: #F4F5F7;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
  svg { transition: transform 0.2s; }
  svg.up { transform: rotate(180deg); }
`;

const VideoScroll = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const VideoCard = styled.div`
  flex-shrink: 0;
  width: 200px;
  max-width: 72vw;
`;

const VideoThumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.95);
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: ${({ $url, theme }) =>
    $url ? `#000 url(${$url}) center/cover` : theme.colors.primaryGradient};
  svg { filter: drop-shadow(0 1px 3px rgba(0,0,0,0.5)); }
  &:active { opacity: ${({ $clickable }) => ($clickable ? 0.85 : 1)}; }
`;

const VideoFrame = styled.iframe`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 0;
  background: #000;
  display: block;
`;

const VideoTitle = styled.p`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 7px;
  line-height: 1.4;
`;

const RvTitle = styled.p`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 14px;
  b { color: ${({ theme }) => theme.colors.primary}; margin-left: 5px; }
`;

const RvWriteBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const RvItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  border-radius: 12px;
  background: #F4F5F7;
  overflow: hidden;
  margin-bottom: 8px;
`;

const RvFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const RvLabel = styled.span`
  position: relative;
  z-index: 1;
  padding-left: 16px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RvRight = styled.span`
  position: relative;
  z-index: 1;
  margin-left: auto;
  padding-right: 14px;
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const RvPct = styled.span`
  font-size: 13.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const RvCnt = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const RvEmpty = styled.p`
  text-align: center;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 8px 0 4px;
`;

const RvComment = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid #F0F1F4;
  &:first-of-type { margin-top: 8px; border-top: 1px solid #F0F1F4; }
  &:last-child { border-bottom: none; }
`;

const RvCTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RvCRec = styled.span`
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const RvCText = styled.p`
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 7px;
`;

const RvCFoot = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 6px;
`;

const NearScroll = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const NearCard = styled.button`
  flex-shrink: 0;
  width: 132px;
  text-align: left;
  background: none;
`;

const NearThumb = styled.div`
  width: 132px;
  height: 92px;
  border-radius: 12px;
  background: ${({ $url }) => ($url ? `#EDEFF3 url(${$url}) center/cover` : '#EDEFF3')};
`;

const NearName = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NearMeta = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const Hint = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 26px 0;
`;
